import { useCallback, useEffect } from 'react'
import { useGetRealChatId } from '@renderer/chat'
import { useUpdateTopViewContext } from '../../view-atoms'
import { MEETING_VIEW_ID } from '../../components/meeting/meeting-view-id'

/**
 * Open the Meeting View's chat drawer by flipping the top meeting entry into
 * its chat state — the meeting stays on screen behind the thread. Mirrors the
 * Action View's drawer hooks (HIG-5977); a no-op unless a meeting entry is on
 * top with a thread to show.
 */
export function useOpenMeetingChatDrawer(): () => void {
  const updateMeetingContext = useUpdateTopViewContext(MEETING_VIEW_ID)
  return useCallback(
    () => updateMeetingContext((prev) => (prev && !prev.chat ? { ...prev, chat: true } : prev)),
    [updateMeetingContext],
  )
}

/** The inverse: dismissing (scrim click, swipe-down, Escape) lands back on the meeting. */
export function useDismissMeetingChatDrawer(): () => void {
  const updateMeetingContext = useUpdateTopViewContext(MEETING_VIEW_ID)
  return useCallback(
    () => updateMeetingContext((prev) => (prev?.chat ? { ...prev, chat: false } : prev)),
    [updateMeetingContext],
  )
}

/**
 * Point the entry's drawer at a conversation and slide it up — what a submit
 * from the meeting composer does instead of pushing a Chat View. A follow-up
 * on the same conversation only re-opens the drawer, so a dismissed thread
 * comes back rather than restarting.
 */
export function useStartMeetingChat(): (conversationId: string) => void {
  const updateMeetingContext = useUpdateTopViewContext(MEETING_VIEW_ID)
  return useCallback(
    (conversationId: string) =>
      updateMeetingContext((prev) => {
        if (!prev) return prev
        if (prev.chatId === conversationId && prev.chat) return prev
        return { ...prev, chatId: conversationId, chat: true, isFreshSubmit: true }
      }),
    [updateMeetingContext],
  )
}

/** Clear the fresh-submit flag once the drawer's thread has settled on it. */
export function useSettleMeetingChatFreshSubmit(): () => void {
  const updateMeetingContext = useUpdateTopViewContext(MEETING_VIEW_ID)
  return useCallback(
    () => updateMeetingContext((prev) => (prev?.isFreshSubmit ? { ...prev, isFreshSubmit: false } : prev)),
    [updateMeetingContext],
  )
}

/**
 * The entry's conversation, resolved across the temp → real id swap. A submit
 * from the meeting composer mints a client-side UUID and reports *that* to
 * `onSubmit`; the stream's `metadata` event renames it moments later and
 * records the mapping.
 *
 * Resolved at read, not just healed in the effect below: everything the drawer
 * hands the id to keys on the *real* one from the rename onward, so a render
 * against the temp id reads the turn as idle — which would settle the
 * fresh-submit flag mid-stream and let the verify re-subscribe over the
 * in-flight reply. The write-back is what survives the surface: it keeps the
 * persisted entry (and a follow-up posted from it) off an id the backend never
 * issued. Mirrors the Chat View's reconciliation, minus the new-chat
 * bookkeeping the drawer has no equivalent of.
 */
export function useResolvedMeetingChatId(chatId: string | undefined): string | undefined {
  const updateMeetingContext = useUpdateTopViewContext(MEETING_VIEW_ID)
  const getRealChatId = useGetRealChatId()
  const realChatId = chatId ? getRealChatId(chatId) : undefined

  useEffect(() => {
    if (!chatId || !realChatId || realChatId === chatId) return
    updateMeetingContext((prev) => (prev?.chatId === chatId ? { ...prev, chatId: realChatId } : prev))
  }, [chatId, realChatId, updateMeetingContext])

  return realChatId
}
